import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import { LogoOnly } from "../../assets/icons/logoOnly";

// Bespoke "bookmark organizer for schools" page. Built to capture a clear GSC
// cluster (Oct 2026): "bookmark organizer for schools", "best way to save
// favorites for schools", "recommended/top bookmark organizer for schools".
// Localized via the `schoolsPage.*` translation keys so it's indexable in every
// site language (see scripts/routes.js — this route is NOT in
// NON_LOCALIZED_ROUTES). The exact-match /bookmark-organizer-for-schools route
// renders this same component with `localized={false}` + a pinned canonical so
// the two URLs don't compete.

const BookmarksForSchools = ({ localized = true, canonicalOverride }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const faqData = [
    { q: t("schoolsPage.faq.bestWay.q"), a: t("schoolsPage.faq.bestWay.a") },
    { q: t("schoolsPage.faq.goodFit.q"), a: t("schoolsPage.faq.goodFit.a") },
    { q: t("schoolsPage.faq.free.q"), a: t("schoolsPage.faq.free.a") },
    { q: t("schoolsPage.faq.separate.q"), a: t("schoolsPage.faq.separate.a") },
    { q: t("schoolsPage.faq.private.q"), a: t("schoolsPage.faq.private.a") },
    { q: t("schoolsPage.faq.import.q"), a: t("schoolsPage.faq.import.a") },
  ];

  useEffect(() => {
    if (location.hash === "#save-favorites") {
      const element = document.getElementById("save-favorites");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  return (
    <>
      <SeoTags
        localized={localized}
        canonical={canonicalOverride}
        title={t("schoolsPage.seo.title")}
        description={t("schoolsPage.seo.description")}
        keywords={t("schoolsPage.seo.keywords")}
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("schoolsPage.seo.schemaName"),
            description: t("schoolsPage.seo.description"),
            url: "https://onbookmarks.com/bookmarks-for-schools",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "EducationApplication",
              operatingSystem: "Web, iOS, Android",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-info-subtle fs-normal text-info px-3 py-2 mb-4">
                  <i className="bi bi-mortarboard me-2"></i>
                  {t("schoolsPage.hero.badge")}
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">{t("schoolsPage.hero.title")}</h1>
              <p className="lead mb-4">{t("schoolsPage.hero.lead")}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t("schoolsPage.hero.ctaPrimary")}
                </Link>
                <Link to="#save-favorites" className="btn btn-outline-primary btn-lg px-4">
                  {t("schoolsPage.hero.ctaSecondary")}
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                {t("schoolsPage.hero.guarantee")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("schoolsPage.trust.organized")}</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-wifi-off h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("schoolsPage.trust.offline")}</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-shield-lock h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">{t("schoolsPage.trust.private")}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">{t("schoolsPage.problem.title")}</h2>
            <p className="lead text-muted">{t("schoolsPage.problem.subtitle")}</p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-danger-subtle rounded-3 mb-3">
                    <i className="bi bi-folder2-open fs-3 text-danger"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.problem.scattered.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.problem.scattered.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-search fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.problem.lost.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.problem.lost.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-secondary-subtle rounded-3 mb-3">
                    <i className="bi bi-shuffle fs-3 text-secondary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.problem.semester.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.problem.semester.body")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best way to save favorites — step guide */}
      <section id="save-favorites" className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">{t("schoolsPage.guide.title")}</h2>
                <p className="lead text-muted">{t("schoolsPage.guide.subtitle")}</p>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">1</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("schoolsPage.guide.step1.title")}</h4>
                          <p className="text-muted mb-0">{t("schoolsPage.guide.step1.body")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">2</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("schoolsPage.guide.step2.title")}</h4>
                          <p className="text-muted mb-0">{t("schoolsPage.guide.step2.body")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex align-items-start">
                        <div
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px", minWidth: "40px" }}
                        >
                          <span className="fw-bold">3</span>
                        </div>
                        <div>
                          <h4 className="h5 mb-2">{t("schoolsPage.guide.step3.title")}</h4>
                          <p className="text-muted mb-3">{t("schoolsPage.guide.step3.body")}</p>
                          <Link
                            to={`${process.env.REACT_APP_APP_URL}/register`}
                            className="btn btn-primary"
                          >
                            {t("schoolsPage.guide.step3.button")}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why it's the recommended organizer */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">{t("schoolsPage.why.title")}</h2>
            <p className="lead text-muted">{t("schoolsPage.why.subtitle")}</p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.organizes.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.why.organizes.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-cash-coin fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.free.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.why.free.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-wifi-off fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.offline.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.why.offline.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-info-subtle rounded-3 mb-3">
                    <i className="bi bi-collection fs-3 text-info"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.spaces.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.why.spaces.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-danger-subtle rounded-3 mb-3">
                    <i className="bi bi-shield-lock fs-3 text-danger"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.private.title")}</h3>
                  <p className="text-muted mb-0">{t("schoolsPage.why.private.body")}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-box-arrow-in-down fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t("schoolsPage.why.import.title")}</h3>
                  <p className="text-muted mb-0">
                    {t("schoolsPage.why.import.body")}{" "}
                    <Link to="/features/import">{t("schoolsPage.why.import.link")}</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for — cross-links */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="h3 fw-bold mb-3">{t("schoolsPage.campus.title")}</h2>
          </div>
          <div className="row g-4 justify-content-center">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-mortarboard fs-1 text-info mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("schoolsPage.campus.students.title")}</h3>
                  <p className="text-muted mb-3">{t("schoolsPage.campus.students.body")}</p>
                  <Link to="/bookmarks-for-students" className="btn btn-sm btn-outline-primary">
                    {t("schoolsPage.campus.students.link")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-journal-text fs-1 text-success mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("schoolsPage.campus.researchers.title")}</h3>
                  <p className="text-muted mb-3">{t("schoolsPage.campus.researchers.body")}</p>
                  <Link to="/bookmarks-for-researchers" className="btn btn-sm btn-outline-primary">
                    {t("schoolsPage.campus.researchers.link")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm text-center">
                <div className="card-body p-4">
                  <i className="bi bi-easel fs-1 text-warning mb-3 d-block"></i>
                  <h3 className="h5 mb-2">{t("schoolsPage.campus.teachers.title")}</h3>
                  <p className="text-muted mb-3">{t("schoolsPage.campus.teachers.body")}</p>
                  <Link to="/bookmarks-for-teachers" className="btn btn-sm btn-outline-primary">
                    {t("schoolsPage.campus.teachers.link")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h2 fw-bold mb-4">{t("schoolsPage.faq.title")}</h2>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-6">
                <div className="h-100">
                  <h3 className="h5 fw-bold mb-3">{faq.q}</h3>
                  <p className="text-muted">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h1 fw-bold mb-4 text-white d-flex align-items-center justify-content-center gap-2">
                <LogoOnly width="32" />
                {t("schoolsPage.finalCta.title")}
              </h2>
              <p className="lead mb-4">{t("schoolsPage.finalCta.lead")}</p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t("schoolsPage.finalCta.primary")}
                </Link>
                <Link to="#save-favorites" className="btn btn-outline-light btn-lg px-4">
                  <i className="bi bi-map me-2"></i>
                  {t("schoolsPage.finalCta.secondary")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BookmarksForSchools;
